import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { ArrowLeft, Home, QrCode, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import { RedeemToken } from '@vaiu/redeem-ux';
import TokenRedemptionLanding from '@/components/TokenRedemptionLanding';

const RedeemPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  // Track if we've already processed the auto-redeem to prevent infinite loops
  const processedRef = useRef(false);
  
  // Parse code from URL parameters or query parameters
  // Supported patterns:
  // - /redeem/ABC123 (URL param)
  // - /redeem?code=ABC123 (query param)
  // Priority: URL params > query params
  const queryParams = new URLSearchParams(location.search);
  const codeParam = params.code || queryParams.get('code');
  
  // State management
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [showManualRedeem, setShowManualRedeem] = useState(false);
  const [redemptionCompleted, setRedemptionCompleted] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);

  // Handle authentication flow
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        // User is not authenticated
        if (codeParam) {
          // Store the intended redemption in sessionStorage with extended timeout (2 hours)
          sessionStorage.setItem('pendingRedemption', JSON.stringify({
            code: codeParam,
            timestamp: Date.now()
          }));
          
          // Show the improved landing page instead of redirecting to login
          setShowLandingPage(true);
          return;
        } else {
          // No code provided, redirect to login with redeem context
          const returnUrl = `${location.pathname}${location.search}`;
          navigate(`/login?forRedeem=true&redirect=${encodeURIComponent(returnUrl)}`);
          return;
        }
      }
      
      // User is authenticated, check for pending redemption or current code
      const storedRedemption = sessionStorage.getItem('pendingRedemption');
      let codeToProcess = codeParam;
      
      if (storedRedemption) {
        try {
          const parsed = JSON.parse(storedRedemption);
          // Extended timeout to 2 hours (120 minutes) for better UX
          if (Date.now() - parsed.timestamp < 120 * 60 * 1000) {
            codeToProcess = parsed.code;
          }
          // Clear the stored redemption
          sessionStorage.removeItem('pendingRedemption');
        } catch (e) {
          console.error('Error parsing stored redemption:', e);
          sessionStorage.removeItem('pendingRedemption');
        }
      }
      
      if (codeToProcess && !processedRef.current) {
        setPendingCode(codeToProcess);
        processedRef.current = true;
      } else if (!codeToProcess) {
        // No code provided, show manual redeem interface
        setShowManualRedeem(true);
      }
    }
  }, [authLoading, user, codeParam, navigate]);

  // Handle successful authentication from landing page
  const handleAuthSuccess = (token: string) => {
    // The TokenRedemptionLanding component handles the login
    // The useEffect above will then process the pending redemption
    setShowLandingPage(false);
    
    toast({
      title: i18n.__('redeem.authSuccess'),
      description: i18n.__('redeem.processing'),
    });
  };

  // Handle back to login from landing page
  const handleBackToLogin = () => {
    setShowLandingPage(false);
    // Pass redeem context and returnUrl so user can return after login
    const returnUrl = codeParam 
      ? `${location.pathname}${location.search}` 
      : location.pathname;
    navigate(`/login?forRedeem=true&redirect=${encodeURIComponent(returnUrl)}`);
  };

  // Show the improved landing page for unauthenticated users
  if (showLandingPage && codeParam) {
    return (
      <TokenRedemptionLanding
        tokenCode={codeParam}
        onAuthSuccess={handleAuthSuccess}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  // Show loading while auth is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{i18n.__('redeem.loading')}</p>
        </div>
      </div>
    );
  }

  // User should be authenticated at this point due to the useEffect above
  if (!user) {
    return null; // This should not happen due to redirect logic
  }

  const handleRedemptionComplete = (result: any) => {
    // Mark redemption as completed but keep pendingCode so RedeemToken can show results
    setRedemptionCompleted(true);
    
    console.log('✅ Redemption completed on RedeemPage:', result);
    
    toast({
      title: i18n.__('redeem.redemptionComplete'),
      description: i18n.__('redeem.redemptionCompleteDesc'),
    });
    
    // For cash token redemptions, redirect to dashboard after a short delay
    // For invoice payments, stay on the current page to show results
    const isInvoicePayment = result.invoicePayment || result.paidInvoice || result.details?.invoicePayment;
    
    if (!isInvoicePayment) {
      // Cash token redemption - redirect to dashboard after showing success message
      setTimeout(() => {
        navigate('/');
      }, 1500); // Give user time to see the success message
    }
    
    // Invoice payments stay on the redeem page to show the payment details
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{i18n.__('redeem.backToHome')}</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            size="sm"
          >
            <Home className="w-4 h-4" />
          </Button>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <QrCode className="w-5 h-5" />
              <span>{i18n.__('vaiu.core.redeem_token')}</span>
            </CardTitle>
          </CardHeader>
                     <CardContent>
             {pendingCode ? (
               <div className="space-y-4">
                 {!redemptionCompleted && (
                   <Alert>
                     <AlertCircle className="h-4 w-4" />
                     <AlertDescription>
                       {i18n.__('redeem.processingCode', { code: pendingCode })}
                     </AlertDescription>
                   </Alert>
                 )}
                 <RedeemToken 
                   initialCode={pendingCode}
                   autoSubmit={!redemptionCompleted}
                   onRedemptionComplete={handleRedemptionComplete}
                 />
               </div>
             ) : showManualRedeem ? (
               <div className="space-y-4">
                 <Alert>
                   <AlertCircle className="h-4 w-4" />
                   <AlertDescription>
                     {i18n.__('redeem.noCode')}
                   </AlertDescription>
                 </Alert>
                 <RedeemToken onRedemptionComplete={handleRedemptionComplete} />
               </div>
             ) : (
               <div className="text-center py-8">
                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                 <p className="text-muted-foreground">{i18n.__('redeem.processingRequest')}</p>
               </div>
             )}
           </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RedeemPage; 